import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Collapse,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ViewSample from "./ViewSample";
import AddSampleDialog from "./AddSampleDialog";
import DuplicateSampleDialog from "./DuplicateSampleDialog";
import DeleteConfirmation from "../common/DeleteConfirmation";
import {
  DeleteOrderSample,
  DuplicateOrderSample,
  UpdateOrderSample,
} from "../../services/OrderService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import useSampleLookups from "./useSampleLookups";

const SamplesTable = ({ samples: samplesProp, accountNumber, orderNumber }) => {
  // Held locally so an inline edit in an expanded row shows immediately
  // without reloading the order. An order can also come back without a
  // samples collection, so default it to a list.
  const [samples, setSamples] = useState(samplesProp || []);

  useEffect(() => {
    setSamples(samplesProp || []);
  }, [samplesProp]);

  // One set of lookup requests for the whole table, shared by the edit dialog
  // and every expanded row.
  const lookups = useSampleLookups(accountNumber);

  const columns = [
    "Order Number",
    "Sample Number",
    "Lab Number",
    "Date Received",
    "Date Collected",
    "Sample Matrix",
    "Lab ID",
    "Rush",
    "Hold",
    "Actions",
  ];
  const columnHeaders = [
    "orderNumber",
    "sampleNumber",
    "labNumber",
    "dateReceived",
    "dateCollected",
    "sampleMatrix",
    "labId",
    "rush",
    "hold",
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // recordids of the currently expanded rows
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [selectedSample, setSelectedSample] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSampleToDelete, setSelectedSampleToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  // Duplicate flow: track which sample is being duplicated and whether
  // the selection dialog is open.
  const [sampleToDuplicate, setSampleToDuplicate] = useState(null);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Toggles the detail row nested under the clicked sample row.
  const handleViewClick = (sampleData) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(sampleData.recordid)) {
        next.delete(sampleData.recordid);
      } else {
        next.add(sampleData.recordid);
      }
      return next;
    });
  };

  // Expands every sample if any are collapsed, otherwise collapses them all.
  const handleToggleExpandAll = () => {
    setExpandedRowIds((prev) =>
      prev.size === samples.length
        ? new Set()
        : new Set(samples.map((sample) => sample.recordid))
    );
  };

  // Saves a single field edited inline in an expanded row through the same
  // update-sample endpoint the Edit Sample dialog uses. The whole sample is
  // sent because that endpoint assigns every field it knows about, so a
  // partial payload would blank the rest. Errors are rethrown so the input
  // can put its previous value back.
  const handleSampleFieldSave = async (sample, field, value) => {
    const updatedSample = { ...sample, [field]: value };

    try {
      const response = await UpdateOrderSample(updatedSample);
      if (response.status !== 200) {
        throw new Error("Update rejected");
      }

      setSamples((prev) =>
        prev.map((item) =>
          item.recordid === sample.recordid ? updatedSample : item
        )
      );
    } catch (error) {
      NotificationManager.error("Error updating sample");
      throw error;
    }
  };

  const handleEditClick = (sampleData) => {
    setSelectedSample(sampleData);
    setIsEditing(true);
    setDialogOpen(true);
  };

  // Clicking the duplicate icon now opens the selection dialog instead of
  // duplicating immediately.
  const handleDuplicateClick = (sampleData) => {
    setSampleToDuplicate(sampleData);
    setIsDuplicateDialogOpen(true);
  };

  const handleDuplicateDialogClose = () => {
    setIsDuplicateDialogOpen(false);
    setSampleToDuplicate(null);
  };

  // Called once the user confirms their selections in DuplicateSampleDialog.
  const handleDuplicateConfirm = async (selection) => {
    const targetSample = sampleToDuplicate;
    setIsDuplicateDialogOpen(false);
    setSampleToDuplicate(null);

    if (!targetSample) {
      return;
    }

    try {
      setLoading(true);
      const response = await DuplicateOrderSample(
        targetSample.orderNumber,
        targetSample.recordid,
        selection
      );
      if (response) {
        const created = response?.results?.length ?? selection.copies ?? 1;
        NotificationManager.success(
          created === 1
            ? "Sample duplicated successfully"
            : `${created} samples created successfully`
        );
        refreshPage();
      } else {
        NotificationManager.error("Error duplicating sample");
      }
    } catch (error) {
      NotificationManager.error("Error duplicating sample");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    // Order with no samples yet: nothing to copy from, start a blank sample 1.
    // Otherwise copy the highest-numbered sample and bump its number.
    let newSample;
    if (samples.length === 0) {
      newSample = { orderNumber, sampleNumber: 1, recordid: null };
    } else {
      const highestSample = samples.reduce((prev, current) =>
        prev.sampleNumber > current.sampleNumber ? prev : current
      );
      newSample = {
        ...highestSample,
        sampleNumber: highestSample.sampleNumber + 1,
        recordid: null,
      };
    }

    // Set this new sample as the selected sample
    setSelectedSample(newSample);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleDeleteClick = (sampleData) => {
    setSelectedSampleToDelete(sampleData);
    setIsDeleteConfirmationOpen(true);
  };

  const refreshPage = () => {
    window.location.reload(false);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteOrderSample(
        selectedSampleToDelete.orderNumber,
        selectedSampleToDelete.recordid
      );
      if (response.results === true) {
        NotificationManager.success("Sample deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setLoading(false);
        refreshPage();
        // Optionally, you can reload the samples or update the state to remove the deleted sample from the list
      } else {
        NotificationManager.error("Error deleting sample");
        setLoading(false);
      }
    } catch (error) {
      NotificationManager.error("Error deleting sample");
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const renderActions = (sampleData) => {
    const isExpanded = expandedRowIds.has(sampleData.recordid);

    return (
      <div style={{ minWidth: "150px" }}>
        <IconButton
          aria-label="edit"
          style={{ color: "orange" }}
          onClick={() => handleEditClick(sampleData)}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          aria-label="duplicate"
          style={{ color: "purple" }}
          onClick={() => handleDuplicateClick(sampleData)}
        >
          <ContentCopyIcon />
        </IconButton>
        <Tooltip title={isExpanded ? "Hide expanded view" : "Expanded view"}>
          <IconButton
            aria-label="view"
            style={{ color: "green" }}
            onClick={() => handleViewClick(sampleData)}
          >
            {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label="delete"
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(sampleData)}
        >
          <DeleteIcon />
        </IconButton>
      </div>
    );
  };

  const allExpanded =
    expandedRowIds.size === samples.length && samples.length > 0;

  return (
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Samples
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          New
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={allExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          onClick={handleToggleExpandAll}
          disabled={samples.length === 0}
        >
          {allExpanded ? "Collapse" : "Expand"}
        </Button>
      </Box>
      <TableContainer>
        <Table sx={{ minWidth: 400 }} size="small" dense>
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {samples
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((sample, index) => {
                const isExpanded = expandedRowIds.has(sample.recordid);

                return (
                  // Fragment so each sample can render its normal row plus an
                  // optional nested detail row directly below it.
                  <React.Fragment key={sample.sampleNumber}>
                    <TableRow
                      sx={{
                        backgroundColor: isExpanded
                          ? "rgba(25, 118, 210, 0.08)"
                          : index % 2 === 0
                            ? "rgba(0, 0, 0, 0.04)"
                            : "white",
                      }}
                    >
                      {columnHeaders.map((column) => (
                        <TableCell key={column}>
                          {column === "dateReceived" ||
                            column === "dateCollected" ? (
                            formatDate(sample[column])
                          ) : column === "rush" || column === "hold" ? (
                            sample[column] ? (
                              "Yes"
                            ) : (
                              "No"
                            )
                          ) : column === "sampleNumber" ? (
                            sample[column] ? (
                              <a
                                href={`/vlp/lab-orders/${sample.orderNumber}/samples/${sample.sampleNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#1976d2",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {sample[column]}
                              </a>
                            ) : (
                              "-"
                            )
                          ) : (
                            sample[column]
                          )}
                        </TableCell>
                      ))}

                      <TableCell>{renderActions(sample)}</TableCell>
                    </TableRow>

                    {/* Nested detail row - only mounted while expanded to keep the DOM light */}
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ my: 1 }}>
                            <ViewSample
                              sampleData={sample}
                              onFieldSave={handleSampleFieldSave}
                              lookups={lookups}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={samples.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <AddSampleDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        sample={selectedSample}
        isEditing={isEditing}
        lookups={lookups}
      />
      <DuplicateSampleDialog
        open={isDuplicateDialogOpen}
        onClose={handleDuplicateDialogClose}
        onConfirm={handleDuplicateConfirm}
        sample={sampleToDuplicate}
      />
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this sample?"
        color="red"
      />
      {loading && <CommonLoader />}
    </Paper>
  );
};

export default SamplesTable;
